import React from 'react';
import { RiStackFill, RiStackLine } from "react-icons/ri";
import { formatPrice } from '../processors';

/**
 * BggListingMetadata
 *
 * Displays key listing metadata for a BGG geeklist item:
 *  - List price + deal score (percent diff vs average price)
 *  - Price type (e.g. FP, SB, BIN)
 *  - Seller (clickable - filters the list to the seller and dismisses the modal)
 *  - Average rating
 *
 * @param {Object} props
 * @param {Object} props.item        - GeeklistItem data
 * @param {number} [props.averagePrice] - Average price from the pricing report
 * @param {Object} [props.dealScore] - Deal score result from the grid calculation, including extras
 * @param {boolean} [props.isAveragePriceLoading] - Whether price-history-dependent metadata is loading
 * @param {Function} [props.onSellerClick] - Called with the seller username when the seller link is clicked
 */
const BggListingMetadata = ({ item, averagePrice, dealScore, isAveragePriceLoading = false, onSellerClick }) => {
    if (!item) return null;

    const listPrice = parseFloat(item.price);
    const hasListPrice = !isNaN(listPrice);
    const dealScoreAverage = dealScore?.averagePrice;
    const score = dealScore?.score;
    const hasDealScore = score !== null && score !== undefined && score !== '' && score !== '?' && score !== 'N/A' && !isNaN(parseFloat(score));
    const displayAverage = dealScoreAverage || averagePrice;

    // Deal percent: positive = below average (good deal), negative = above average.
    // Prefer the grid's deal score result so listings with extras match the table.
    let dealPercent = hasDealScore ? parseFloat(score) : null;
    let dealColor = 'var(--color-text-primary)';
    if (dealPercent !== null) {
        if (dealPercent > 50) dealColor = '#008800';
        else if (dealPercent > 10) dealColor = '#00AA00';
        else if (dealPercent > -10) dealColor = '#ed8d11';
        else dealColor = '#880000';
    }

    const seller = item.username;
    const avgRating = item?.stats?.average;

    const handleSellerClick = (e) => {
        e.preventDefault();
        if (onSellerClick && seller) onSellerClick(seller);
    };

    return (
        <div className="listing-metadata">
            <div className="listing-metadata-item listing-metadata-price">
                <span className="listing-metadata-label">List Price</span>
                <div className="listing-metadata-price-row">
                    <span className="price-value">
                        {hasListPrice ? `$${listPrice.toFixed(2)}` : '—'}
                    </span>
                    {isAveragePriceLoading && hasListPrice ? (
                        <span
                            className="listing-metadata-placeholder"
                            title="Looking up average price"
                            aria-label="Looking up average price"
                        >
                            —%
                        </span>
                    ) : dealPercent !== null && (
                        <span
                            className="listing-metadata-deal"
                            style={{ color: dealColor }}
                            title={`Average price${dealScore?.includesExtras ? ' including extras' : ''}: $${formatPrice(displayAverage)}`}
                        >
                            {dealPercent > 0 ? '-' : '+'}{Math.abs(dealPercent).toFixed(0)}%
                            {dealScore?.includesExtras && (
                                <span className="listing-metadata-extras" title={dealScore.allPriced ? 'Includes priced extras' : 'Some extras were not priced'}>
                                    {dealScore.allPriced ? <RiStackFill /> : <RiStackLine />}
                                </span>
                            )}
                        </span>
                    )}
                </div>
            </div>

            <div className="listing-metadata-item">
                <span className="listing-metadata-label">Price Type</span>
                <span className="listing-metadata-value">{item.priceType || '—'}</span>
            </div>

            <div className="listing-metadata-item">
                <span className="listing-metadata-label">Seller</span>
                {seller ? (
                    <button
                        type="button"
                        className="link-button listing-metadata-value"
                        onClick={handleSellerClick}
                        title={`Filter listings to ${seller}`}
                    >
                        {seller}
                    </button>
                ) : (
                    <span className="listing-metadata-value">—</span>
                )}
            </div>

            <div className="listing-metadata-item">
                <span className="listing-metadata-label">Avg Rating</span>
                <span className="listing-metadata-value">
                    {avgRating ? parseFloat(avgRating).toFixed(2) : '—'}
                </span>
            </div>
        </div>
    );
};

export default BggListingMetadata;
